import { createFileRoute } from '@tanstack/react-router';
import { Edit, Loader2, Lock, Settings } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    type GenerationsRolloutPreflight,
    GenerationsRolloutPreflightReport,
} from '@apps/web/components/admin/generations_rollout_preflight_report';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Switch } from '@apps/web/components/ui/switch';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { toast } from '@apps/web/lib/app_notifications';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import { type ColumnDef } from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminSyncSettings,
    useUpdateAdminSyncSettings,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminSyncSettingsRead,
    type components,
    type DataSourceType,
    type SyncCapability,
    type SyncEntitySettings,
    type WellSkySyncStrategy,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/sync-settings')({
    validateSearch: (
        search: Record<string, unknown>,
    ): { readonly editAccountId?: string } => ({
        editAccountId:
            typeof search['editAccountId'] === 'string' &&
            search['editAccountId'].length > 0
                ? search['editAccountId']
                : undefined,
    }),
    component: SyncSettingsPage,
    head: () => ({
        meta: [
            {
                title: 'Sync Settings | Phoebe Admin',
            },
        ],
    }),
});

function EntitySyncRow({
    entityName,
    entity,
    providerName,
    deltaEnabled,
    hourlyEnabled,
    dailyEnabled,
    onDeltaToggle,
    onHourlyToggle,
    onDailyToggle,
    disabled,
    forcedByAuthOnly,
}: {
    entityName: string;
    entity: SyncEntitySettings;
    providerName: string;
    deltaEnabled: boolean;
    hourlyEnabled: boolean;
    dailyEnabled: boolean;
    onDeltaToggle: (enabled: boolean) => void;
    onHourlyToggle: (enabled: boolean) => void;
    onDailyToggle: (enabled: boolean) => void;
    disabled?: boolean;
    forcedByAuthOnly?: boolean;
}) {
    const renderSwitch = (
        modeName: string,
        capability: SyncCapability,
        enabled: boolean,
        onToggle: (enabled: boolean) => void,
    ) => {
        const isConfigurable = capability.state === 'configurable';
        const isAlways = capability.state === 'always';
        const isNever = capability.state === 'never';

        const tooltipContent = forcedByAuthOnly
            ? 'Locked by auth-only sync mode'
            : isAlways
              ? `Always enabled for ${providerName}`
              : isNever
                ? `${providerName} does not support this sync mode`
                : null;

        const showLock = !isConfigurable || disabled;
        const switchElement = (
            <div className="flex items-center justify-center">
                <div className="relative flex items-center">
                    {showLock && (
                        <Lock className="text-muted-foreground absolute -left-4 h-3 w-3" />
                    )}
                    <Switch
                        checked={enabled}
                        onCheckedChange={onToggle}
                        disabled={disabled || !isConfigurable}
                        aria-label={`${entityName} ${modeName} sync`}
                    />
                </div>
            </div>
        );

        if (tooltipContent) {
            return (
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        {switchElement}
                    </TooltipTrigger>
                    <TooltipContent>{tooltipContent}</TooltipContent>
                </Tooltip>
            );
        }

        return switchElement;
    };

    return (
        <Fragment>
            <Label className="text-sm leading-tight">{entityName}</Label>
            {renderSwitch('delta', entity.delta, deltaEnabled, onDeltaToggle)}
            {renderSwitch(
                'hourly',
                entity.hourly,
                hourlyEnabled,
                onHourlyToggle,
            )}
            {renderSwitch('daily', entity.daily, dailyEnabled, onDailyToggle)}
        </Fragment>
    );
}

type WellSkyProviderSettings = {
    readonly caregiver_sync_strategy: string;
    readonly client_sync_strategy: string;
    readonly shift_sync_strategy: string;
    readonly caregiver_sync_delta: boolean;
    readonly client_sync_delta: boolean;
    readonly shift_sync_delta: boolean;
    readonly location_ids: ReadonlyArray<number> | null;
    readonly include_care_plans?: boolean;
    readonly care_plan_sources?: ReadonlyArray<string>;
    readonly recommendations_only_available?: boolean;
    readonly recommendations_no_overtime?: boolean;
    readonly recommendations_has_all_must_haves?: boolean;
    readonly recommendations_include_all_offices?: boolean;
    readonly sync_communities_as_clients?: boolean;
    readonly require_caregiver_confirmation_on_assign?: boolean;
};

type HHAExchangeProviderSettings = {
    readonly include_skilled?: boolean;
    readonly include_non_terminated_caregivers_as_active?: boolean;
    readonly include_poc_in_care_plan?: boolean;
    readonly skip_declined_caregivers_sync?: boolean;
    readonly caregiver_absence_writeback_type_id?: number | null;
    readonly include_extended_client_data_in_care_plan?: boolean;
    readonly case_note_writeback_enabled?: boolean;
    readonly link_client_profile_switches?: boolean;
};

type AxisCareProviderSettings = {
    readonly voice_call_logging_enabled?: boolean;
    readonly voice_call_writeback_destination?: 'call_log' | 'person_note';
    readonly include_priority_note?: boolean;
    readonly include_care_plan_form?: boolean;
    readonly care_plan_form_section_allowlist?: Array<string>;
    readonly caregiver_status_tag_allowlist?: Array<string>;
    readonly ignored_caregiver_status_labels?: Array<string>;
    readonly assume_unchecked_caregiver_attributes?: boolean;
    readonly assume_unchecked_client_attributes?: boolean;
};

type GenerationsProviderSettings = {
    readonly read_enabled: boolean;
    readonly api_enabled: boolean;
    readonly portal_fallback_enabled: boolean;
    readonly portal_schedule_read_enabled: boolean;
    readonly availability_read_enabled: boolean;
    readonly client_note_read_enabled: boolean;
    readonly client_note_write_enabled: boolean;
    readonly assignment_write_enabled: boolean;
    readonly unassignment_write_enabled: boolean;
    readonly callout_vacancy_write_enabled: boolean;
    readonly payroll_write_enabled: boolean;
    readonly recurrence_write_enabled: boolean;
    readonly time_update_write_enabled: boolean;
    readonly clock_read_enabled: boolean;
    readonly clock_in_write_enabled: boolean;
    readonly clock_out_write_enabled: boolean;
    readonly rollout_preflight?: GenerationsRolloutPreflight;
};

type AxisCareModificationReasonRule =
    components['schemas']['ModificationReasonRule'];

const WELLSKY_STRATEGY_OPTIONS = {
    caregivers: ['scraping', 'rest_api', 'profile_api'] as const,
    clients: ['rest_api'] as const,
    shifts: ['client_calendar_events', 'rest_api', 'scraping'] as const,
} as const;

const WELLSKY_CARE_PLAN_LOCATION_OPTIONS = [
    { value: 'care_goals', label: 'Care Goals' },
    { value: 'client_note', label: 'Client Note' },
    { value: 'full_assessment', label: 'Full Assessment' },
    { value: 'care_coordination_notes', label: 'Care Coordination Notes' },
] as const;

const formatStrategy = (strategy: string) => {
    return strategy.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

type EntitySyncModesState = {
    readonly delta: boolean;
    readonly hourly: boolean;
    readonly daily: boolean;
};

type WellSkyCarePlanLocation =
    | 'care_goals'
    | 'client_note'
    | 'full_assessment'
    | 'care_coordination_notes';

type WellSkySettingsState = {
    readonly caregiverSyncStrategy: WellSkySyncStrategy;
    readonly clientSyncStrategy: WellSkySyncStrategy;
    readonly shiftSyncStrategy: WellSkySyncStrategy;
    readonly includeCarePlans: boolean;
    readonly carePlanSources: Array<WellSkyCarePlanLocation>;
    readonly recommendationsOnlyAvailable: boolean;
    readonly recommendationsNoOvertime: boolean;
    readonly recommendationsHasAllMustHaves: boolean;
    readonly recommendationsIncludeAllOffices: boolean;
    readonly syncCommunitiesAsClients: boolean;
    readonly requireCaregiverConfirmationOnAssign: boolean;
};

type HHAExchangeSettingsState = {
    readonly includeSkilled: boolean;
    readonly includeNonTerminatedCaregiversAsActive: boolean;
    readonly includePocInCarePlan: boolean;
    readonly skipDeclinedCaregiversSync: boolean;
    readonly caregiverAbsenceWritebackTypeId: string;
    readonly includeExtendedClientDataInCarePlan: boolean;
    readonly caseNoteWritebackEnabled: boolean;
    readonly linkClientProfileSwitches: boolean;
};

type AxisCareSettingsState = {
    readonly voiceCallLoggingEnabled: boolean;
    readonly voiceCallWritebackDestination: 'call_log' | 'person_note';
    readonly includePriorityNote: boolean;
    readonly includeCarePlanForm: boolean;
    readonly sectionAllowlist: Array<string>;
    readonly caregiverStatusTagAllowlist: Array<string>;
    readonly ignoredCaregiverStatusLabels: Array<string>;
    readonly assumeUncheckedCaregiverAttributes: boolean;
    readonly assumeUncheckedClientAttributes: boolean;
};

function GenerationsSettingsSection({
    settings,
    assignmentWriteEnabled,
    unassignmentWriteEnabled,
    calloutVacancyWriteEnabled,
    recurrenceWriteEnabled,
    onAssignmentWriteEnabledChange,
    onUnassignmentWriteEnabledChange,
    onCalloutVacancyWriteEnabledChange,
    onRecurrenceWriteEnabledChange,
    disabled,
}: {
    settings: GenerationsProviderSettings | null;
    assignmentWriteEnabled: boolean;
    unassignmentWriteEnabled: boolean;
    calloutVacancyWriteEnabled: boolean;
    recurrenceWriteEnabled: boolean;
    onAssignmentWriteEnabledChange: (enabled: boolean) => void;
    onUnassignmentWriteEnabledChange: (enabled: boolean) => void;
    onCalloutVacancyWriteEnabledChange: (enabled: boolean) => void;
    onRecurrenceWriteEnabledChange: (enabled: boolean) => void;
    disabled: boolean;
}) {
    const readEnabled = settings?.read_enabled ?? false;
    const scheduleReadEnabled = settings?.portal_schedule_read_enabled ?? false;
    const clientNoteReadEnabled = settings?.client_note_read_enabled ?? false;
    const writesAllowed = readEnabled && scheduleReadEnabled;
    // The server refuses this combination too. Mirroring it here is what
    // makes the rule discoverable: the save error is a generic toast, so an
    // enabled switch that always fails would read as a bug.
    const recurrenceAllowed =
        writesAllowed && assignmentWriteEnabled && unassignmentWriteEnabled;
    // A callout has no write of its own: it reuses the unassignment write, so
    // that gate is its prerequisite and the server refuses the pair too.
    const calloutVacancyAllowed = writesAllowed && unassignmentWriteEnabled;

    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium">Generations Writeback</h4>
            <GenerationsRolloutPreflightReport
                preflight={settings?.rollout_preflight ?? null}
            />
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <Label>Provider reads</Label>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 px-1.5 text-[10px]',
                            readEnabled
                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                        )}
                    >
                        {readEnabled ? 'Ready' : 'Required'}
                    </Badge>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <Label>Charting schedule reads</Label>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 px-1.5 text-[10px]',
                            scheduleReadEnabled
                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                        )}
                    >
                        {scheduleReadEnabled ? 'Ready' : 'Required'}
                    </Badge>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <Label>Client note reads</Label>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 px-1.5 text-[10px]',
                            clientNoteReadEnabled
                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                        )}
                    >
                        {clientNoteReadEnabled
                            ? 'Ready'
                            : 'Required for client notes'}
                    </Badge>
                </div>
                <p className="text-muted-foreground text-xs">
                    Enable the prerequisite read gates in the integration
                    credentials before allowing writeback.
                </p>
            </div>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label htmlFor="generations-assignment-write-enabled">
                            Assign exact occurrence
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            Allows a certified single-occurrence assignment.
                        </p>
                    </div>
                    <Switch
                        id="generations-assignment-write-enabled"
                        checked={assignmentWriteEnabled}
                        onCheckedChange={onAssignmentWriteEnabledChange}
                        disabled={disabled || !writesAllowed}
                        aria-label="Generations assignment write enabled"
                    />
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label htmlFor="generations-unassignment-write-enabled">
                            Unassign exact occurrence
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            Allows a certified single-occurrence unassignment.
                        </p>
                    </div>
                    <Switch
                        id="generations-unassignment-write-enabled"
                        checked={unassignmentWriteEnabled}
                        onCheckedChange={onUnassignmentWriteEnabledChange}
                        disabled={disabled || !writesAllowed}
                        aria-label="Generations unassignment write enabled"
                    />
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label htmlFor="generations-callout-vacancy-write-enabled">
                            Open the visit when a caregiver calls out
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            Removes the caregiver from that one visit in
                            Generations so it shows as open. Generations has
                            nowhere safe to record why, so the callout reason
                            stays in Phoebe only. Requires unassignment, whose
                            write it reuses.
                        </p>
                    </div>
                    <Switch
                        id="generations-callout-vacancy-write-enabled"
                        checked={calloutVacancyWriteEnabled}
                        onCheckedChange={onCalloutVacancyWriteEnabledChange}
                        disabled={
                            disabled ||
                            (!calloutVacancyAllowed &&
                                !calloutVacancyWriteEnabled)
                        }
                        aria-label="Generations callout vacancy write enabled"
                    />
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label htmlFor="generations-recurrence-write-enabled">
                            Fill several occurrences at once
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            Allows one action to fill the specific occurrences
                            of a repeating visit that a coordinator selected.
                            Requires assignment and unassignment so a partly
                            written batch can be rolled back.
                        </p>
                    </div>
                    <Switch
                        id="generations-recurrence-write-enabled"
                        checked={recurrenceWriteEnabled}
                        onCheckedChange={onRecurrenceWriteEnabledChange}
                        disabled={
                            disabled ||
                            (!recurrenceAllowed && !recurrenceWriteEnabled)
                        }
                        aria-label="Generations recurrence write enabled"
                    />
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label>Move one visit’s times</Label>
                        <p className="text-muted-foreground text-xs">
                            Unavailable today. The Generations editor refused
                            exact readback after the owned ABSDEMO update, so
                            this cannot be enabled until that vendor response
                            has a reviewed contract.
                        </p>
                    </div>
                    <Badge variant="outline">Unavailable</Badge>
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label>Record a note on a client</Label>
                        <p className="text-muted-foreground text-xs">
                            Unavailable today. The sandbox role could add and
                            read back a note, but Generations refused exact
                            cleanup. This cannot be enabled until an authorized
                            inverse is certified.
                        </p>
                    </div>
                    <Badge variant="outline">Unavailable</Badge>
                </div>
            </div>
            <p className="text-muted-foreground text-xs">
                Payroll and clock controls are not certified for editing from
                this screen.
            </p>
        </div>
    );
}

function GenerationsProviderSettingsSummary({
    settings,
}: {
    settings: GenerationsProviderSettings;
}) {
    const statuses: Array<readonly [string, boolean]> = [
        ['Provider reads', settings.read_enabled],
        ['API reads', settings.api_enabled],
        ['Portal fallback', settings.portal_fallback_enabled],
        ['Schedule reads', settings.portal_schedule_read_enabled],
        ['Availability reads', settings.availability_read_enabled],
        ['Client note reads', settings.client_note_read_enabled],
        ['Client note write', settings.client_note_write_enabled],
        ['Assignment', settings.assignment_write_enabled],
        ['Unassignment', settings.unassignment_write_enabled],
        ['Callout vacancy', settings.callout_vacancy_write_enabled],
        ['Payroll', settings.payroll_write_enabled],
        ['Recurrence', settings.recurrence_write_enabled],
        ['Time update', settings.time_update_write_enabled],
        ['Clock read', settings.clock_read_enabled],
        ['Clock in', settings.clock_in_write_enabled],
        ['Clock out', settings.clock_out_write_enabled],
    ];

    return (
        <div className="flex flex-col gap-0.5 text-xs">
            {statuses.map(([label, enabled]) => (
                <div key={label} className="flex items-center gap-1.5">
                    <span className="text-muted-foreground w-24">{label}</span>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 px-1.5 text-[10px]',
                            enabled
                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                : '',
                        )}
                    >
                        {enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                </div>
            ))}
        </div>
    );
}

export function AxisCareModificationReasonRulesBadge({
    rules,
}: {
    rules: ReadonlyArray<AxisCareModificationReasonRule>;
}) {
    if (rules.length === 0) {
        return null;
    }

    return (
        <Badge
            variant="outline"
            className="h-5 w-fit bg-amber-100 px-1.5 text-[10px] text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
        >
            {`${rules.length} reason${rules.length === 1 ? '' : 's'} → Cancelled`}
        </Badge>
    );
}

function WellSkySettingsSection({
    settings,
    locationIds,
    onChange,
    disabled,
}: {
    settings: WellSkySettingsState;
    locationIds: ReadonlyArray<number> | null;
    onChange: (settings: WellSkySettingsState) => void;
    disabled?: boolean;
}) {
    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium">WellSky Sync Strategy</h4>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <Label className="w-20 text-sm">Caregivers</Label>
                    <Select
                        value={settings.caregiverSyncStrategy}
                        onValueChange={(value) =>
                            onChange({
                                ...settings,
                                caregiverSyncStrategy:
                                    value as WellSkySyncStrategy,
                            })
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger className="w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {WELLSKY_STRATEGY_OPTIONS.caregivers.map(
                                (strategy) => (
                                    <SelectItem key={strategy} value={strategy}>
                                        {formatStrategy(strategy)}
                                    </SelectItem>
                                ),
                            )}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <Label className="w-20 text-sm">Clients</Label>
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <div>
                                <Select
                                    value={settings.clientSyncStrategy}
                                    onValueChange={(value) =>
                                        onChange({
                                            ...settings,
                                            clientSyncStrategy:
                                                value as WellSkySyncStrategy,
                                        })
                                    }
                                    disabled={true}
                                >
                                    <SelectTrigger className="w-48">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {WELLSKY_STRATEGY_OPTIONS.clients.map(
                                            (strategy) => (
                                                <SelectItem
                                                    key={strategy}
                                                    value={strategy}
                                                >
                                                    {formatStrategy(strategy)}
                                                </SelectItem>
                                            ),
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            Only REST API is implemented for clients
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">
                            Sync Communities as Clients
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Include is_client=false and is_place=true WellSky
                            records in client sync
                        </span>
                    </div>
                    <Switch
                        checked={settings.syncCommunitiesAsClients}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                syncCommunitiesAsClients: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">
                            Require Caregiver Confirmation on Assign
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            When Phoebe assigns or fills a shift, require
                            caregiver confirmation in WellSky so it shows as
                            Pending Confirmation instead of Scheduled
                        </span>
                    </div>
                    <Switch
                        checked={settings.requireCaregiverConfirmationOnAssign}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                requireCaregiverConfirmationOnAssign: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <Label className="w-20 text-sm">Shifts</Label>
                    <Select
                        value={settings.shiftSyncStrategy}
                        onValueChange={(value) =>
                            onChange({
                                ...settings,
                                shiftSyncStrategy: value as WellSkySyncStrategy,
                            })
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger className="w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {WELLSKY_STRATEGY_OPTIONS.shifts.map((strategy) => (
                                <SelectItem key={strategy} value={strategy}>
                                    {formatStrategy(strategy)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {locationIds && locationIds.length > 0 && (
                    <div className="border-t pt-2">
                        <span className="text-muted-foreground text-xs">
                            Location IDs:{' '}
                        </span>
                        <span className="text-xs">
                            {locationIds.join(', ')}
                        </span>
                    </div>
                )}
            </div>
            <h4 className="text-sm font-medium">Care Plan Settings</h4>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Include Care Plans</Label>
                        <span className="text-muted-foreground text-xs">
                            Fetch and sync care plan data from WellSky
                        </span>
                    </div>
                    <Switch
                        checked={settings.includeCarePlans}
                        onCheckedChange={(checked) =>
                            onChange({ ...settings, includeCarePlans: checked })
                        }
                        disabled={disabled}
                    />
                </div>
                {settings.includeCarePlans && (
                    <div className="space-y-2">
                        <div className="flex flex-col">
                            <Label className="text-sm">Care Plan Sources</Label>
                            <span className="text-muted-foreground text-xs">
                                Where to fetch care plan data from in WellSky
                            </span>
                        </div>
                        <div className="space-y-2">
                            {WELLSKY_CARE_PLAN_LOCATION_OPTIONS.map(
                                (option) => (
                                    <div
                                        key={option.value}
                                        className="flex items-center justify-between"
                                    >
                                        <Label className="text-sm font-normal">
                                            {option.label}
                                        </Label>
                                        <Switch
                                            checked={settings.carePlanSources.includes(
                                                option.value as WellSkyCarePlanLocation,
                                            )}
                                            onCheckedChange={(checked) => {
                                                const newSources = checked
                                                    ? [
                                                          ...settings.carePlanSources,
                                                          option.value as WellSkyCarePlanLocation,
                                                      ]
                                                    : settings.carePlanSources.filter(
                                                          (s) =>
                                                              s !==
                                                              option.value,
                                                      );
                                                // Prevent unchecking the last source
                                                if (newSources.length === 0) {
                                                    return;
                                                }
                                                onChange({
                                                    ...settings,
                                                    carePlanSources: newSources,
                                                });
                                            }}
                                            disabled={disabled}
                                        />
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                )}
            </div>
            <h4 className="text-sm font-medium">Caregiver Recommendations</h4>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Include All Offices</Label>
                        <span className="text-muted-foreground text-xs">
                            Include caregivers from all offices when making
                            recommendations
                        </span>
                    </div>
                    <Switch
                        checked={settings.recommendationsIncludeAllOffices}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                recommendationsIncludeAllOffices: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Only Available</Label>
                        <span className="text-muted-foreground text-xs">
                            Only recommend caregivers marked as available
                        </span>
                    </div>
                    <Switch
                        checked={settings.recommendationsOnlyAvailable}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                recommendationsOnlyAvailable: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">No Overtime</Label>
                        <span className="text-muted-foreground text-xs">
                            Exclude caregivers who would go into overtime
                        </span>
                    </div>
                    <Switch
                        checked={settings.recommendationsNoOvertime}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                recommendationsNoOvertime: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Has All Must Haves</Label>
                        <span className="text-muted-foreground text-xs">
                            Only recommend caregivers with all required skills
                        </span>
                    </div>
                    <Switch
                        checked={settings.recommendationsHasAllMustHaves}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                recommendationsHasAllMustHaves: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
}

function HHAExchangeSettingsSection({
    settings,
    onChange,
    disabled,
}: {
    settings: HHAExchangeSettingsState;
    onChange: (settings: HHAExchangeSettingsState) => void;
    disabled?: boolean;
}) {
    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium">HHAeXchange Settings</h4>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Include Skilled</Label>
                        <span className="text-muted-foreground text-xs">
                            Include skilled nursing caregivers and visits in
                            sync
                        </span>
                    </div>
                    <Switch
                        checked={settings.includeSkilled}
                        onCheckedChange={(checked) =>
                            onChange({ ...settings, includeSkilled: checked })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">
                            Include Non-Terminated as Active
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Sync HHAeXchange non-terminated caregiver roster and
                            treat those caregivers as active in Phoebe.
                        </span>
                    </div>
                    <Switch
                        checked={
                            settings.includeNonTerminatedCaregiversAsActive
                        }
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                includeNonTerminatedCaregiversAsActive: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">
                            Include POC in Care Plan
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Fetch Plan of Care data and include in client care
                            plans
                        </span>
                    </div>
                    <Switch
                        checked={settings.includePocInCarePlan}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                includePocInCarePlan: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="hhaexchange-skip-declined-caregivers"
                            className="text-sm"
                        >
                            Skip Declined Caregivers
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Skip declined-caregiver restrictions when this
                            HHAeXchange account does not authorize the endpoint.
                        </span>
                    </div>
                    <Switch
                        id="hhaexchange-skip-declined-caregivers"
                        checked={settings.skipDeclinedCaregiversSync}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                skipDeclinedCaregiversSync: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Skip Declined Caregivers"
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="hhaexchange-extended-client-care-plan"
                            className="text-sm"
                        >
                            Include Extended Client Care Plan Data
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Include alerts, emergency preparedness, and the
                            completed initial-call note in client care plans.
                        </span>
                    </div>
                    <Switch
                        id="hhaexchange-extended-client-care-plan"
                        checked={settings.includeExtendedClientDataInCarePlan}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                includeExtendedClientDataInCarePlan: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Include Extended Client Care Plan Data"
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="hhaexchange-caregiver-absence-type-id"
                            className="text-sm"
                        >
                            Caregiver Absence Type ID
                        </Label>
                        <span className="text-muted-foreground max-w-lg text-xs">
                            Tenant-validated, non-PTO absence type used only for
                            whole-day unavailability. Phoebe never applies PTO
                            or removes caregivers from visits. Blank disables
                            HHAeXchange writeback.
                        </span>
                    </div>
                    <Input
                        id="hhaexchange-caregiver-absence-type-id"
                        type="number"
                        min={1}
                        step={1}
                        className="w-64"
                        value={settings.caregiverAbsenceWritebackTypeId}
                        onChange={(event) =>
                            onChange({
                                ...settings,
                                caregiverAbsenceWritebackTypeId:
                                    event.target.value,
                            })
                        }
                        placeholder="Absence type ID"
                        disabled={disabled}
                        aria-label="Caregiver Absence Type ID"
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="hhaexchange-case-note-writeback"
                            className="text-sm"
                        >
                            Write Case Notes
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Create caregiver Case Refusal notes when caregivers
                            decline and Case Accepted notes when Phoebe fills an
                            outreach shift in HHAeXchange.
                        </span>
                    </div>
                    <Switch
                        id="hhaexchange-case-note-writeback"
                        checked={settings.caseNoteWritebackEnabled}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                caseNoteWritebackEnabled: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Write Case Notes"
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="hhaexchange-link-client-profile-switches"
                            className="text-sm"
                        >
                            Link Client Profile Switches
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            When the vendor re-issues a client under a new
                            profile, link it to the existing client instead of
                            creating a duplicate, so client memory carries over.
                        </span>
                    </div>
                    <Switch
                        id="hhaexchange-link-client-profile-switches"
                        checked={settings.linkClientProfileSwitches}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                linkClientProfileSwitches: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Link Client Profile Switches"
                    />
                </div>
            </div>
        </div>
    );
}

function AxisCareSettingsSection({
    settings,
    onChange,
    disabled,
}: {
    settings: AxisCareSettingsState;
    onChange: (settings: AxisCareSettingsState) => void;
    disabled?: boolean;
}) {
    const [sectionAllowlistText, setSectionAllowlistText] = useState(
        settings.sectionAllowlist.join('\n'),
    );
    const [
        caregiverStatusTagAllowlistText,
        setCaregiverStatusTagAllowlistText,
    ] = useState(settings.caregiverStatusTagAllowlist.join('\n'));
    const [
        ignoredCaregiverStatusLabelsText,
        setIgnoredCaregiverStatusLabelsText,
    ] = useState(settings.ignoredCaregiverStatusLabels.join('\n'));
    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium">AxisCare Settings</h4>
            <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="axiscare-voice-call-logging-enabled"
                            className="text-sm"
                        >
                            Write Phoebe Voice Calls
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Write completed Phoebe outreach calls back to
                            AxisCare.
                        </span>
                    </div>
                    <Switch
                        id="axiscare-voice-call-logging-enabled"
                        checked={settings.voiceCallLoggingEnabled}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                voiceCallLoggingEnabled: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Write Phoebe Voice Calls"
                    />
                </div>
                {settings.voiceCallLoggingEnabled && (
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-col">
                            <Label
                                htmlFor="axiscare-voice-call-writeback-destination"
                                className="text-sm"
                            >
                                Write calls as
                            </Label>
                            <span className="text-muted-foreground text-xs">
                                Person notes (client and caregiver notes)
                                include the summary and related caregiver,
                                clients, and categories.
                            </span>
                        </div>
                        <Select
                            value={settings.voiceCallWritebackDestination}
                            onValueChange={(value) => {
                                if (
                                    value !== 'call_log' &&
                                    value !== 'person_note'
                                ) {
                                    throw new Error(
                                        `Unexpected AxisCare writeback destination: ${value}`,
                                    );
                                }
                                onChange({
                                    ...settings,
                                    voiceCallWritebackDestination: value,
                                });
                            }}
                            disabled={disabled}
                        >
                            <SelectTrigger
                                id="axiscare-voice-call-writeback-destination"
                                className="w-40"
                                aria-label="Write calls as"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="call_log">
                                    Call Log
                                </SelectItem>
                                <SelectItem value="person_note">
                                    Person Note
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="axiscare-caregiver-status-tags"
                            className="text-sm"
                        >
                            Caregiver Status Tags
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Add an AxisCare caregiver status to Phoebe tags only
                            when its label appears here. One status label per
                            line; case-insensitive, whitespace-trimmed. Leave
                            empty to sync tags from caregiver classes only.
                        </span>
                    </div>
                    <Textarea
                        id="axiscare-caregiver-status-tags"
                        aria-label="Caregiver Status Tags"
                        value={caregiverStatusTagAllowlistText}
                        placeholder={'Family Caregiver\nLRI Caregiver'}
                        rows={3}
                        disabled={disabled}
                        className="font-mono"
                        onChange={(e) => {
                            const value = e.target.value;
                            setCaregiverStatusTagAllowlistText(value);
                            onChange({
                                ...settings,
                                caregiverStatusTagAllowlist: value
                                    .split(/\r?\n/)
                                    .map((label) => label.trim())
                                    .filter((label) => label.length > 0),
                            });
                        }}
                    />
                    <span className="text-muted-foreground text-xs">
                        {settings.caregiverStatusTagAllowlist.length} status
                        label(s) configured
                    </span>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="axiscare-ignored-caregiver-status-labels"
                            className="text-sm"
                        >
                            Ignored Caregiver Status Labels
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Skip caregivers entirely during sync when their
                            AxisCare status label appears here. One status label
                            per line; case-insensitive, whitespace-trimmed.
                            Leave empty to sync all caregivers.
                        </span>
                    </div>
                    <Textarea
                        id="axiscare-ignored-caregiver-status-labels"
                        aria-label="Ignored Caregiver Status Labels"
                        value={ignoredCaregiverStatusLabelsText}
                        placeholder="Active - Lead"
                        rows={3}
                        disabled={disabled}
                        className="font-mono"
                        onChange={(e) => {
                            const value = e.target.value;
                            setIgnoredCaregiverStatusLabelsText(value);
                            onChange({
                                ...settings,
                                ignoredCaregiverStatusLabels: value
                                    .split(/\r?\n/)
                                    .map((label) => label.trim())
                                    .filter((label) => label.length > 0),
                            });
                        }}
                    />
                    <span className="text-muted-foreground text-xs">
                        {settings.ignoredCaregiverStatusLabels.length} status
                        label(s) configured
                    </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">Include Priority Note</Label>
                        <span className="text-muted-foreground text-xs">
                            Include AxisCare client priority notes in synced
                            care plans
                        </span>
                    </div>
                    <Switch
                        checked={settings.includePriorityNote}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                includePriorityNote: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label className="text-sm">
                            Include Care Plan Form
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Sync the AxisCare Care Plan form into the client’s
                            care plan field
                        </span>
                    </div>
                    <Switch
                        checked={settings.includeCarePlanForm}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                includeCarePlanForm: checked,
                            })
                        }
                        disabled={disabled}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="axiscare-assume-unchecked-caregiver-attributes"
                            className="text-sm"
                        >
                            Assume Unchecked Caregiver Attributes
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Treat caregiver attributes with neither yes nor no
                            checked as having the tag; only an explicit no
                            excludes it
                        </span>
                    </div>
                    <Switch
                        id="axiscare-assume-unchecked-caregiver-attributes"
                        checked={settings.assumeUncheckedCaregiverAttributes}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                assumeUncheckedCaregiverAttributes: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Assume Unchecked Caregiver Attributes"
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <Label
                            htmlFor="axiscare-assume-unchecked-client-attributes"
                            className="text-sm"
                        >
                            Assume Unchecked Client Attributes
                        </Label>
                        <span className="text-muted-foreground text-xs">
                            Treat client attributes with neither yes nor no
                            checked as having the tag; only an explicit no
                            excludes it
                        </span>
                    </div>
                    <Switch
                        id="axiscare-assume-unchecked-client-attributes"
                        checked={settings.assumeUncheckedClientAttributes}
                        onCheckedChange={(checked) =>
                            onChange({
                                ...settings,
                                assumeUncheckedClientAttributes: checked,
                            })
                        }
                        disabled={disabled}
                        aria-label="Assume Unchecked Client Attributes"
                    />
                </div>
                {settings.includeCarePlanForm && (
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col">
                            <Label className="text-sm">
                                Care Plan Form Sections & Fields
                            </Label>
                            <span className="text-muted-foreground text-xs">
                                Only these AxisCare form tabs or fields will be
                                synced. Leave empty to sync everything. One tab
                                title or field label per line; case-insensitive,
                                whitespace-trimmed.
                            </span>
                        </div>
                        <Textarea
                            value={sectionAllowlistText}
                            placeholder={
                                'Client Information\nResponsible Parties\nHealth Care Providers'
                            }
                            rows={4}
                            disabled={disabled}
                            className="font-mono"
                            onChange={(e) => {
                                const value = e.target.value;
                                setSectionAllowlistText(value);
                                onChange({
                                    ...settings,
                                    sectionAllowlist: value
                                        .split(/\r?\n/)
                                        .map((s) => s.trim())
                                        .filter((s) => s.length > 0),
                                });
                            }}
                        />
                        <span className="text-muted-foreground text-xs">
                            {settings.sectionAllowlist.length} section(s)
                            configured
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export function EditSyncSettingsDialog({
    settings,
    open,
    onOpenChange,
    onSave,
}: {
    settings: AdminSyncSettingsRead;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: () => Promise<unknown>;
}) {
    const { updateSyncSettings } = useUpdateAdminSyncSettings();
    const [isSaving, setIsSaving] = useState(false);
    const [syncEnabled, setSyncEnabled] = useState(settings.sync_enabled);
    const [onlyAuthSyncEnabled, setOnlyAuthSyncEnabled] = useState(
        settings.only_auth_sync_enabled,
    );

    // Per-entity sync mode state
    const [careCoordinators, setCareCoordinators] =
        useState<EntitySyncModesState>({
            delta: settings.care_coordinators.delta.enabled,
            hourly: settings.care_coordinators.hourly.enabled,
            daily: settings.care_coordinators.daily.enabled,
        });
    const [caregivers, setCaregivers] = useState<EntitySyncModesState>({
        delta: settings.caregivers.delta.enabled,
        hourly: settings.caregivers.hourly.enabled,
        daily: settings.caregivers.daily.enabled,
    });
    const [clients, setClients] = useState<EntitySyncModesState>({
        delta: settings.clients.delta.enabled,
        hourly: settings.clients.hourly.enabled,
        daily: settings.clients.daily.enabled,
    });
    const [shifts, setShifts] = useState<EntitySyncModesState>({
        delta: settings.shifts.delta.enabled,
        hourly: settings.shifts.hourly.enabled,
        daily: settings.shifts.daily.enabled,
    });
    const [authorizations, setAuthorizations] = useState<EntitySyncModesState>({
        delta: settings.authorizations.delta.enabled,
        hourly: settings.authorizations.hourly.enabled,
        daily: settings.authorizations.daily.enabled,
    });

    const [daysLookback, setDaysLookback] = useState(settings.days_lookback);
    const [daysLookforward, setDaysLookforward] = useState(
        settings.days_lookforward,
    );
    const [dailySyncDaysLookforward, setDailySyncDaysLookforward] = useState<
        number | ''
    >(settings.daily_sync_days_lookforward);

    const providerName = getDataSourceDisplayName(settings.data_source_type);
    const isWellSky = settings.data_source_type === 'wellsky';
    const isHHAExchange = settings.data_source_type === 'hhaexchange';
    const isAxisCare = settings.data_source_type === 'axiscare';
    const isGenerations = settings.data_source_type === 'generations_homecare';
    const wellSkyProviderSettings = isWellSky
        ? (settings.provider_settings as WellSkyProviderSettings | null)
        : null;
    const hhaExchangeProviderSettings = isHHAExchange
        ? (settings.provider_settings as HHAExchangeProviderSettings | null)
        : null;
    const axisCareProviderSettings = isAxisCare
        ? (settings.provider_settings as AxisCareProviderSettings | null)
        : null;
    const generationsProviderSettings = isGenerations
        ? (settings.provider_settings as GenerationsProviderSettings | null)
        : null;
    const [
        generationsAssignmentWriteEnabled,
        setGenerationsAssignmentWriteEnabled,
    ] = useState(
        generationsProviderSettings?.assignment_write_enabled ?? false,
    );
    const [
        generationsUnassignmentWriteEnabled,
        setGenerationsUnassignmentWriteEnabled,
    ] = useState(
        generationsProviderSettings?.unassignment_write_enabled ?? false,
    );
    const [
        generationsCalloutVacancyWriteEnabled,
        setGenerationsCalloutVacancyWriteEnabled,
    ] = useState(
        generationsProviderSettings?.callout_vacancy_write_enabled ?? false,
    );
    const [
        generationsRecurrenceWriteEnabled,
        setGenerationsRecurrenceWriteEnabled,
    ] = useState(
        generationsProviderSettings?.recurrence_write_enabled ?? false,
    );
    const [wellSkySettings, setWellSkySettings] =
        useState<WellSkySettingsState>(() => ({
            caregiverSyncStrategy:
                (wellSkyProviderSettings?.caregiver_sync_strategy as WellSkySyncStrategy) ??
                'profile_api',
            clientSyncStrategy:
                (wellSkyProviderSettings?.client_sync_strategy as WellSkySyncStrategy) ??
                'rest_api',
            shiftSyncStrategy:
                (wellSkyProviderSettings?.shift_sync_strategy as WellSkySyncStrategy) ??
                'client_calendar_events',
            includeCarePlans:
                wellSkyProviderSettings?.include_care_plans ?? true,
            carePlanSources:
                (wellSkyProviderSettings?.care_plan_sources as Array<WellSkyCarePlanLocation>) ?? [
                    'full_assessment',
                ],
            recommendationsOnlyAvailable:
                wellSkyProviderSettings?.recommendations_only_available ?? true,
            recommendationsNoOvertime:
                wellSkyProviderSettings?.recommendations_no_overtime ?? true,
            recommendationsHasAllMustHaves:
                wellSkyProviderSettings?.recommendations_has_all_must_haves ??
                true,
            recommendationsIncludeAllOffices:
                wellSkyProviderSettings?.recommendations_include_all_offices ??
                false,
            syncCommunitiesAsClients:
                wellSkyProviderSettings?.sync_communities_as_clients ?? false,
            requireCaregiverConfirmationOnAssign:
                wellSkyProviderSettings?.require_caregiver_confirmation_on_assign ??
                false,
        }));

    const [hhaExchangeSettings, setHHAExchangeSettings] =
        useState<HHAExchangeSettingsState>(() => ({
            includeSkilled:
                hhaExchangeProviderSettings?.include_skilled ?? false,
            includeNonTerminatedCaregiversAsActive:
                hhaExchangeProviderSettings?.include_non_terminated_caregivers_as_active ??
                false,
            includePocInCarePlan:
                hhaExchangeProviderSettings?.include_poc_in_care_plan ?? false,
            skipDeclinedCaregiversSync:
                hhaExchangeProviderSettings?.skip_declined_caregivers_sync ??
                false,
            caregiverAbsenceWritebackTypeId:
                hhaExchangeProviderSettings?.caregiver_absence_writeback_type_id?.toString() ??
                '',
            includeExtendedClientDataInCarePlan:
                hhaExchangeProviderSettings?.include_extended_client_data_in_care_plan ??
                false,
            caseNoteWritebackEnabled:
                hhaExchangeProviderSettings?.case_note_writeback_enabled ??
                false,
            linkClientProfileSwitches:
                hhaExchangeProviderSettings?.link_client_profile_switches ??
                false,
        }));
    const [axisCareSettings, setAxisCareSettings] =
        useState<AxisCareSettingsState>(() => ({
            voiceCallLoggingEnabled:
                axisCareProviderSettings?.voice_call_logging_enabled ?? true,
            voiceCallWritebackDestination:
                axisCareProviderSettings?.voice_call_writeback_destination ??
                'call_log',
            includePriorityNote:
                axisCareProviderSettings?.include_priority_note ?? false,
            includeCarePlanForm:
                axisCareProviderSettings?.include_care_plan_form ?? false,
            sectionAllowlist:
                axisCareProviderSettings?.care_plan_form_section_allowlist ??
                [],
            caregiverStatusTagAllowlist:
                axisCareProviderSettings?.caregiver_status_tag_allowlist ?? [],
            ignoredCaregiverStatusLabels:
                axisCareProviderSettings?.ignored_caregiver_status_labels ?? [],
            assumeUncheckedCaregiverAttributes:
                axisCareProviderSettings?.assume_unchecked_caregiver_attributes ??
                false,
            assumeUncheckedClientAttributes:
                axisCareProviderSettings?.assume_unchecked_client_attributes ??
                false,
        }));

    const handleOnlyAuthSyncToggle = (next: boolean) => {
        setOnlyAuthSyncEnabled(next);
        if (next) {
            // Mirror the backend force-on-save: only Clients/Daily and
            // Authorizations/Daily are on; everything else is off. Snap the
            // local UI state so the table reflects what will be persisted.
            const allOff = { delta: false, hourly: false, daily: false };
            setCareCoordinators(allOff);
            setCaregivers(allOff);
            setClients({ delta: false, hourly: false, daily: true });
            setShifts(allOff);
            setAuthorizations({ delta: false, hourly: false, daily: true });
        }
    };

    const authorizationSyncSupported = settings.authorization_sync_supported;

    // Validation: care plans enabled requires at least one source
    const isWellSkyValid =
        !isWellSky ||
        !wellSkySettings.includeCarePlans ||
        wellSkySettings.carePlanSources.length > 0;
    const hhaExchangeAbsenceTypeId =
        hhaExchangeSettings.caregiverAbsenceWritebackTypeId.trim();
    const isHHAExchangeValid =
        !isHHAExchange ||
        hhaExchangeAbsenceTypeId === '' ||
        /^[1-9]\d*$/.test(hhaExchangeAbsenceTypeId);
    const isFormValid = isWellSkyValid && isHHAExchangeValid;

    function normalizeDailySyncDaysLookforward() {
        return Math.min(90, Math.max(30, dailySyncDaysLookforward || 30));
    }

    const handleSave = async () => {
        const normalizedDailySyncDaysLookforward =
            normalizeDailySyncDaysLookforward();
        setIsSaving(true);
        try {
            await updateSyncSettings(settings.data_source_account_id, {
                sync_enabled: syncEnabled,
                care_coordinators: careCoordinators,
                caregivers,
                clients,
                shifts,
                days_lookback: daysLookback,
                days_lookforward: daysLookforward,
                daily_sync_days_lookforward: normalizedDailySyncDaysLookforward,
                ...(authorizationSyncSupported && {
                    only_auth_sync_enabled: onlyAuthSyncEnabled,
                    authorizations,
                }),
                ...(isGenerations && {
                    generations_assignment_write_enabled:
                        generationsAssignmentWriteEnabled,
                    generations_unassignment_write_enabled:
                        generationsUnassignmentWriteEnabled,
                    generations_callout_vacancy_write_enabled:
                        generationsCalloutVacancyWriteEnabled,
                    generations_recurrence_write_enabled:
                        generationsRecurrenceWriteEnabled,
                    // The live editor refused exact readback. Always clear a
                    // legacy gate; the API independently rejects enablement.
                    generations_time_update_write_enabled: false,
                    // The provider has an experimental low-level transport,
                    // but no certified inverse. Always clear a legacy gate;
                    // the API independently rejects attempts to enable it.
                    generations_client_note_write_enabled: false,
                }),
                ...(isWellSky && {
                    wellsky_caregiver_sync_strategy:
                        wellSkySettings.caregiverSyncStrategy,
                    wellsky_client_sync_strategy:
                        wellSkySettings.clientSyncStrategy,
                    wellsky_shift_sync_strategy:
                        wellSkySettings.shiftSyncStrategy,
                    wellsky_include_care_plans:
                        wellSkySettings.includeCarePlans,
                    wellsky_care_plan_sources: wellSkySettings.carePlanSources,
                    wellsky_recommendations_only_available:
                        wellSkySettings.recommendationsOnlyAvailable,
                    wellsky_recommendations_no_overtime:
                        wellSkySettings.recommendationsNoOvertime,
                    wellsky_recommendations_has_all_must_haves:
                        wellSkySettings.recommendationsHasAllMustHaves,
                    wellsky_recommendations_include_all_offices:
                        wellSkySettings.recommendationsIncludeAllOffices,
                    wellsky_sync_communities_as_clients:
                        wellSkySettings.syncCommunitiesAsClients,
                    wellsky_require_caregiver_confirmation_on_assign:
                        wellSkySettings.requireCaregiverConfirmationOnAssign,
                }),
                ...(isHHAExchange && {
                    hhaexchange_include_skilled:
                        hhaExchangeSettings.includeSkilled,
                    hhaexchange_include_non_terminated_caregivers_as_active:
                        hhaExchangeSettings.includeNonTerminatedCaregiversAsActive,
                    hhaexchange_include_poc_in_care_plan:
                        hhaExchangeSettings.includePocInCarePlan,
                    hhaexchange_skip_declined_caregivers_sync:
                        hhaExchangeSettings.skipDeclinedCaregiversSync,
                    hhaexchange_caregiver_absence_writeback_type_id:
                        hhaExchangeAbsenceTypeId === ''
                            ? null
                            : Number(hhaExchangeAbsenceTypeId),
                    hhaexchange_include_extended_client_data_in_care_plan:
                        hhaExchangeSettings.includeExtendedClientDataInCarePlan,
                    hhaexchange_case_note_writeback_enabled:
                        hhaExchangeSettings.caseNoteWritebackEnabled,
                    hhaexchange_link_client_profile_switches:
                        hhaExchangeSettings.linkClientProfileSwitches,
                }),
                ...(isAxisCare && {
                    axiscare_voice_call_logging_enabled:
                        axisCareSettings.voiceCallLoggingEnabled,
                    axiscare_voice_call_writeback_destination:
                        axisCareSettings.voiceCallWritebackDestination,
                    axiscare_include_priority_note:
                        axisCareSettings.includePriorityNote,
                    axiscare_include_care_plan_form:
                        axisCareSettings.includeCarePlanForm,
                    axiscare_care_plan_form_section_allowlist:
                        axisCareSettings.sectionAllowlist,
                    axiscare_caregiver_status_tag_allowlist:
                        axisCareSettings.caregiverStatusTagAllowlist,
                    axiscare_ignored_caregiver_status_labels:
                        axisCareSettings.ignoredCaregiverStatusLabels,
                    axiscare_assume_unchecked_caregiver_attributes:
                        axisCareSettings.assumeUncheckedCaregiverAttributes,
                    axiscare_assume_unchecked_client_attributes:
                        axisCareSettings.assumeUncheckedClientAttributes,
                }),
            });
            toast.success('Sync settings updated successfully', {
                persistNotification: false,
            });
            await onSave();
            onOpenChange(false);
        } catch {
            toast.error('Failed to update sync settings', {
                persistNotification: false,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit Sync Settings</DialogTitle>
                    <DialogDescription>
                        {settings.organization_internal_name ||
                            settings.organization_name}{' '}
                        ({providerName})
                    </DialogDescription>
                </DialogHeader>
                <div className="-mr-3 max-h-[60vh] space-y-6 overflow-y-auto py-4 pr-3">
                    {isGenerations && (
                        <GenerationsSettingsSection
                            settings={generationsProviderSettings}
                            assignmentWriteEnabled={
                                generationsAssignmentWriteEnabled
                            }
                            unassignmentWriteEnabled={
                                generationsUnassignmentWriteEnabled
                            }
                            calloutVacancyWriteEnabled={
                                generationsCalloutVacancyWriteEnabled
                            }
                            recurrenceWriteEnabled={
                                generationsRecurrenceWriteEnabled
                            }
                            onAssignmentWriteEnabledChange={(enabled) => {
                                setGenerationsAssignmentWriteEnabled(enabled);
                                if (!enabled) {
                                    // Dropping a rollback gate must visibly
                                    // drop the dependent switch, not leave a
                                    // request the server will reject.
                                    setGenerationsRecurrenceWriteEnabled(false);
                                }
                            }}
                            onUnassignmentWriteEnabledChange={(enabled) => {
                                setGenerationsUnassignmentWriteEnabled(enabled);
                                if (!enabled) {
                                    setGenerationsRecurrenceWriteEnabled(false);
                                    // The callout vacancy issues the
                                    // unassignment write itself, so dropping
                                    // that gate drops this one too.
                                    setGenerationsCalloutVacancyWriteEnabled(
                                        false,
                                    );
                                }
                            }}
                            onCalloutVacancyWriteEnabledChange={
                                setGenerationsCalloutVacancyWriteEnabled
                            }
                            onRecurrenceWriteEnabledChange={
                                setGenerationsRecurrenceWriteEnabled
                            }
                            disabled={isSaving}
                        />
                    )}
                    <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                        <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                                <Label htmlFor="sync-jobs-enabled">
                                    Sync jobs enabled
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Turning this off skips this datasource
                                    account for scheduled syncs and non-forced
                                    sync requests.
                                </p>
                            </div>
                            <Switch
                                id="sync-jobs-enabled"
                                checked={syncEnabled}
                                onCheckedChange={setSyncEnabled}
                                disabled={isSaving}
                                aria-label="Sync jobs enabled"
                            />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h4 className="text-sm font-medium">
                            Sync Modes by Entity
                        </h4>
                        <div className="bg-muted/50 rounded-md border p-3">
                            <div className="grid grid-cols-4 items-center gap-x-3 gap-y-5">
                                <div />
                                <Label className="text-muted-foreground flex justify-center text-xs">
                                    Delta
                                </Label>
                                <Label className="text-muted-foreground flex justify-center text-xs">
                                    Hourly
                                </Label>
                                <Label className="text-muted-foreground flex justify-center text-xs">
                                    Daily
                                </Label>
                                <EntitySyncRow
                                    entityName="Care Coordinators"
                                    entity={settings.care_coordinators}
                                    providerName={providerName}
                                    deltaEnabled={careCoordinators.delta}
                                    hourlyEnabled={careCoordinators.hourly}
                                    dailyEnabled={careCoordinators.daily}
                                    onDeltaToggle={(v) =>
                                        setCareCoordinators({
                                            ...careCoordinators,
                                            delta: v,
                                        })
                                    }
                                    onHourlyToggle={(v) =>
                                        setCareCoordinators({
                                            ...careCoordinators,
                                            hourly: v,
                                        })
                                    }
                                    onDailyToggle={(v) =>
                                        setCareCoordinators({
                                            ...careCoordinators,
                                            daily: v,
                                        })
                                    }
                                    disabled={isSaving || onlyAuthSyncEnabled}
                                    forcedByAuthOnly={onlyAuthSyncEnabled}
                                />
                                <EntitySyncRow
                                    entityName="Caregivers"
                                    entity={settings.caregivers}
                                    providerName={providerName}
                                    deltaEnabled={caregivers.delta}
                                    hourlyEnabled={caregivers.hourly}
                                    dailyEnabled={caregivers.daily}
                                    onDeltaToggle={(v) =>
                                        setCaregivers({
                                            ...caregivers,
                                            delta: v,
                                        })
                                    }
                                    onHourlyToggle={(v) =>
                                        setCaregivers({
                                            ...caregivers,
                                            hourly: v,
                                        })
                                    }
                                    onDailyToggle={(v) =>
                                        setCaregivers({
                                            ...caregivers,
                                            daily: v,
                                        })
                                    }
                                    disabled={isSaving || onlyAuthSyncEnabled}
                                    forcedByAuthOnly={onlyAuthSyncEnabled}
                                />
                                <EntitySyncRow
                                    entityName="Clients"
                                    entity={settings.clients}
                                    providerName={providerName}
                                    deltaEnabled={clients.delta}
                                    hourlyEnabled={clients.hourly}
                                    dailyEnabled={clients.daily}
                                    onDeltaToggle={(v) =>
                                        setClients({ ...clients, delta: v })
                                    }
                                    onHourlyToggle={(v) =>
                                        setClients({ ...clients, hourly: v })
                                    }
                                    onDailyToggle={(v) =>
                                        setClients({ ...clients, daily: v })
                                    }
                                    disabled={isSaving || onlyAuthSyncEnabled}
                                    forcedByAuthOnly={onlyAuthSyncEnabled}
                                />
                                <EntitySyncRow
                                    entityName="Shifts"
                                    entity={settings.shifts}
                                    providerName={providerName}
                                    deltaEnabled={shifts.delta}
                                    hourlyEnabled={shifts.hourly}
                                    dailyEnabled={shifts.daily}
                                    onDeltaToggle={(v) =>
                                        setShifts({ ...shifts, delta: v })
                                    }
                                    onHourlyToggle={(v) =>
                                        setShifts({ ...shifts, hourly: v })
                                    }
                                    onDailyToggle={(v) =>
                                        setShifts({ ...shifts, daily: v })
                                    }
                                    disabled={isSaving || onlyAuthSyncEnabled}
                                    forcedByAuthOnly={onlyAuthSyncEnabled}
                                />
                                {authorizationSyncSupported && (
                                    <EntitySyncRow
                                        entityName="Auths"
                                        entity={settings.authorizations}
                                        providerName={providerName}
                                        deltaEnabled={authorizations.delta}
                                        hourlyEnabled={authorizations.hourly}
                                        dailyEnabled={authorizations.daily}
                                        onDeltaToggle={(v) =>
                                            setAuthorizations({
                                                ...authorizations,
                                                delta: v,
                                            })
                                        }
                                        onHourlyToggle={(v) =>
                                            setAuthorizations({
                                                ...authorizations,
                                                hourly: v,
                                            })
                                        }
                                        onDailyToggle={(v) =>
                                            setAuthorizations({
                                                ...authorizations,
                                                daily: v,
                                            })
                                        }
                                        disabled={
                                            isSaving || onlyAuthSyncEnabled
                                        }
                                        forcedByAuthOnly={onlyAuthSyncEnabled}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-sm font-medium">Sync Window</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="lookback">Days Lookback</Label>
                                <Input
                                    id="lookback"
                                    type="number"
                                    min={0}
                                    max={30}
                                    value={daysLookback}
                                    onChange={(e) =>
                                        setDaysLookback(
                                            parseInt(e.target.value, 10) || 0,
                                        )
                                    }
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lookforward">
                                    Days Lookforward
                                </Label>
                                <Input
                                    id="lookforward"
                                    type="number"
                                    min={1}
                                    max={60}
                                    value={daysLookforward}
                                    onChange={(e) =>
                                        setDaysLookforward(
                                            parseInt(e.target.value, 10) || 7,
                                        )
                                    }
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="daily-sync-lookforward"
                                    className="whitespace-nowrap"
                                >
                                    Daily Sync Lookahead (Days)
                                </Label>
                                <Input
                                    id="daily-sync-lookforward"
                                    type="number"
                                    min={30}
                                    max={90}
                                    value={dailySyncDaysLookforward}
                                    onChange={(e) => {
                                        setDailySyncDaysLookforward(
                                            e.target.value === ''
                                                ? ''
                                                : parseInt(e.target.value, 10),
                                        );
                                    }}
                                    onBlur={() => {
                                        setDailySyncDaysLookforward(
                                            normalizeDailySyncDaysLookforward(),
                                        );
                                    }}
                                    disabled={isSaving}
                                />
                            </div>
                        </div>
                    </div>
                    {authorizationSyncSupported && (
                        <div className="bg-muted/50 space-y-3 rounded-md border p-3">
                            <div className="flex items-start justify-between gap-4">
                                <div className="space-y-1">
                                    <Label htmlFor="only-auth-sync-enabled">
                                        Only auth sync enabled
                                    </Label>
                                    <p className="text-muted-foreground text-xs">
                                        When on, only Clients/Daily and
                                        Authorizations/Daily sync. All other
                                        entity sync modes are forced off.
                                    </p>
                                </div>
                                <Switch
                                    id="only-auth-sync-enabled"
                                    checked={onlyAuthSyncEnabled}
                                    onCheckedChange={handleOnlyAuthSyncToggle}
                                    disabled={isSaving}
                                    aria-label="Only auth sync enabled"
                                />
                            </div>
                        </div>
                    )}
                    {isWellSky && (
                        <WellSkySettingsSection
                            settings={wellSkySettings}
                            locationIds={
                                wellSkyProviderSettings?.location_ids ?? null
                            }
                            onChange={setWellSkySettings}
                            disabled={isSaving}
                        />
                    )}
                    {isHHAExchange && (
                        <HHAExchangeSettingsSection
                            settings={hhaExchangeSettings}
                            onChange={setHHAExchangeSettings}
                            disabled={isSaving}
                        />
                    )}
                    {isAxisCare && (
                        <AxisCareSettingsSection
                            settings={axisCareSettings}
                            onChange={setAxisCareSettings}
                            disabled={isSaving}
                        />
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || !isFormValid}
                    >
                        {isSaving && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CapabilityBadge({ capability }: { capability: SyncCapability }) {
    const label =
        capability.state === 'configurable'
            ? capability.enabled
                ? 'On'
                : 'Off'
            : capability.enabled
              ? capability.state === 'always'
                  ? 'Always'
                  : 'On'
              : capability.state === 'never'
                ? 'Never'
                : 'Off';

    const colorClass = capability.enabled
        ? capability.state === 'always'
            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300'
            : 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
        : 'bg-muted text-muted-foreground';

    return (
        <Badge variant="outline" className={cn('text-xs', colorClass)}>
            {capability.state !== 'configurable' && (
                <Lock className="mr-1 h-3 w-3" />
            )}
            {label}
        </Badge>
    );
}

export function EntitySyncBadges({
    entity,
    syncEnabled,
}: {
    entity: SyncEntitySettings;
    syncEnabled: boolean;
}) {
    if (!syncEnabled) {
        return (
            <div className="flex flex-col gap-1">
                <Badge
                    variant="outline"
                    className="bg-muted text-muted-foreground w-fit text-[10px]"
                >
                    Mode settings
                </Badge>
                <span className="text-muted-foreground text-[10px]">
                    Apply after re-enable
                </span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
                <span className="text-muted-foreground w-10 text-[10px]">
                    Delta
                </span>
                <CapabilityBadge capability={entity.delta} />
            </div>
            <div className="flex items-center gap-1">
                <span className="text-muted-foreground w-10 text-[10px]">
                    Hourly
                </span>
                <CapabilityBadge capability={entity.hourly} />
            </div>
            <div className="flex items-center gap-1">
                <span className="text-muted-foreground w-10 text-[10px]">
                    Daily
                </span>
                <CapabilityBadge capability={entity.daily} />
            </div>
        </div>
    );
}

export function AuthorizationSyncBadges({
    settings,
}: {
    settings: AdminSyncSettingsRead;
}) {
    if (!settings.authorization_sync_supported) {
        return (
            <Badge
                variant="outline"
                className="bg-muted text-muted-foreground w-fit text-xs"
            >
                N/A
            </Badge>
        );
    }

    return (
        <EntitySyncBadges
            entity={settings.authorizations}
            syncEnabled={settings.sync_enabled}
        />
    );
}

export function AuthOnlyStatusBadge({
    settings,
}: {
    settings: AdminSyncSettingsRead;
}) {
    if (
        !settings.authorization_sync_supported ||
        !settings.only_auth_sync_enabled
    ) {
        return null;
    }

    return (
        <Badge
            variant="outline"
            className="h-5 bg-amber-100 px-1.5 text-[10px] text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
        >
            Auth-only
        </Badge>
    );
}

export function SyncJobsStatus({
    syncEnabled,
    syncFailureCount,
}: {
    syncEnabled: boolean;
    syncFailureCount: number;
}) {
    return (
        <div className="flex flex-col gap-1">
            <Badge
                variant="outline"
                className={cn(
                    'w-fit text-xs',
                    syncEnabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                        : 'bg-muted text-muted-foreground',
                )}
            >
                {syncEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <span className="text-muted-foreground text-xs">
                Failures: {syncFailureCount}
            </span>
        </div>
    );
}

function SyncSettingsPage() {
    const { editAccountId } = Route.useSearch();
    const navigate = Route.useNavigate();
    const [search, setSearch] = useState('');
    const [dataSourceFilter, setDataSourceFilter] = useState<
        DataSourceType | 'all'
    >('all');
    const [editingSettings, setEditingSettings] =
        useState<AdminSyncSettingsRead | null>(null);
    const openedEditAccountIdRef = useRef<string | null>(null);

    const { data, isLoading, mutate } = useAdminSyncSettings({
        search: search || undefined,
        data_source_type:
            dataSourceFilter === 'all' ? undefined : dataSourceFilter,
    });

    const openSettingsEditor = useCallback(
        async (settingsToEdit: AdminSyncSettingsRead) => {
            try {
                const latestData = await mutate();
                const latestSettings = latestData?.sync_settings.find(
                    (syncSettings) =>
                        syncSettings.data_source_account_id ===
                        settingsToEdit.data_source_account_id,
                );
                setEditingSettings(latestSettings ?? settingsToEdit);
            } catch {
                setEditingSettings(settingsToEdit);
            }
        },
        [mutate],
    );

    useEffect(() => {
        if (
            !editAccountId ||
            !data?.sync_settings ||
            openedEditAccountIdRef.current === editAccountId
        ) {
            return;
        }

        const settingsToEdit = data.sync_settings.find(
            (syncSettings) =>
                syncSettings.data_source_account_id === editAccountId,
        );
        if (!settingsToEdit) {
            void navigate({
                replace: true,
                search: (current) => ({
                    ...current,
                    editAccountId: undefined,
                }),
            });
            return;
        }

        openedEditAccountIdRef.current = editAccountId;
        void openSettingsEditor(settingsToEdit);
        void navigate({
            replace: true,
            search: (current) => ({
                ...current,
                editAccountId: undefined,
            }),
        });
    }, [data?.sync_settings, editAccountId, navigate, openSettingsEditor]);

    const columns = useMemo(
        (): Array<ColumnDef<AdminSyncSettingsRead>> => [
            {
                accessorKey: 'organization_name',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Organization"
                    />
                ),
                cell: ({ row }) => {
                    const name = row.original.organization_name;
                    const internalName =
                        row.original.organization_internal_name;
                    return (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium">
                                    {internalName || name}
                                </span>
                                <AuthOnlyStatusBadge settings={row.original} />
                            </div>
                            {internalName && (
                                <span className="text-muted-foreground text-xs">
                                    {name}
                                </span>
                            )}
                        </div>
                    );
                },
                enableSorting: true,
            },
            {
                accessorKey: 'data_source_type',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Data Source"
                    />
                ),
                cell: ({ row }) => {
                    return (
                        <Badge variant="outline">
                            {getDataSourceDisplayName(
                                row.original.data_source_type,
                            )}
                        </Badge>
                    );
                },
                enableSorting: true,
            },
            {
                id: 'sync_jobs',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Sync Jobs" />
                ),
                cell: ({ row }) => (
                    <SyncJobsStatus
                        syncEnabled={row.original.sync_enabled}
                        syncFailureCount={row.original.sync_failure_count}
                    />
                ),
            },
            {
                id: 'care_coordinators_sync',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Care Coordinators"
                    />
                ),
                cell: ({ row }) => (
                    <EntitySyncBadges
                        entity={row.original.care_coordinators}
                        syncEnabled={row.original.sync_enabled}
                    />
                ),
            },
            {
                id: 'caregivers_sync',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Caregivers" />
                ),
                cell: ({ row }) => (
                    <EntitySyncBadges
                        entity={row.original.caregivers}
                        syncEnabled={row.original.sync_enabled}
                    />
                ),
            },
            {
                id: 'clients_sync',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Clients" />
                ),
                cell: ({ row }) => (
                    <EntitySyncBadges
                        entity={row.original.clients}
                        syncEnabled={row.original.sync_enabled}
                    />
                ),
            },
            {
                id: 'shifts_sync',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Shifts" />
                ),
                cell: ({ row }) => (
                    <EntitySyncBadges
                        entity={row.original.shifts}
                        syncEnabled={row.original.sync_enabled}
                    />
                ),
            },
            {
                id: 'authorizations_sync',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Auths" />
                ),
                cell: ({ row }) => (
                    <AuthorizationSyncBadges settings={row.original} />
                ),
            },
            {
                id: 'provider_settings',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Provider Settings"
                    />
                ),
                cell: ({ row }) => {
                    const dataSourceType = row.original.data_source_type;

                    if (dataSourceType === 'wellsky') {
                        const settings = row.original
                            .provider_settings as WellSkyProviderSettings | null;
                        if (!settings) {
                            return (
                                <span className="text-muted-foreground/70">
                                    —
                                </span>
                            );
                        }
                        const carePlanSources =
                            settings.care_plan_sources ?? [];
                        const carePlanSourceLabels = carePlanSources
                            .map(
                                (source) =>
                                    WELLSKY_CARE_PLAN_LOCATION_OPTIONS.find(
                                        (opt) => opt.value === source,
                                    )?.label ?? source,
                            )
                            .sort();
                        return (
                            <div className="flex flex-col gap-0.5 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-16">
                                        Caregivers
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className="h-5 px-1.5 text-[10px]"
                                    >
                                        {formatStrategy(
                                            settings.caregiver_sync_strategy,
                                        )}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-16">
                                        Clients
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className="h-5 px-1.5 text-[10px]"
                                    >
                                        {formatStrategy(
                                            settings.client_sync_strategy,
                                        )}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-16">
                                        Communities
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.sync_communities_as_clients
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.sync_communities_as_clients
                                            ? 'On'
                                            : 'Off'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-16">
                                        Shifts
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className="h-5 px-1.5 text-[10px]"
                                    >
                                        {formatStrategy(
                                            settings.shift_sync_strategy,
                                        )}
                                    </Badge>
                                </div>
                                {carePlanSourceLabels.length > 0 && (
                                    <div className="flex items-start gap-1.5">
                                        <span className="text-muted-foreground w-16">
                                            Care Plans
                                        </span>
                                        <div className="flex flex-wrap gap-0.5">
                                            {carePlanSourceLabels.map(
                                                (label) => (
                                                    <Badge
                                                        key={label}
                                                        variant="outline"
                                                        className="h-5 px-1.5 text-[10px]"
                                                    >
                                                        {label}
                                                    </Badge>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    if (dataSourceType === 'hhaexchange') {
                        const settings = row.original
                            .provider_settings as HHAExchangeProviderSettings | null;
                        if (!settings) {
                            return (
                                <span className="text-muted-foreground/70">
                                    —
                                </span>
                            );
                        }
                        return (
                            <div className="flex flex-col gap-0.5 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-12">
                                        Skilled
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.include_skilled
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.include_skilled
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-14">
                                        Roster
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.include_non_terminated_caregivers_as_active
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.include_non_terminated_caregivers_as_active
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-12">
                                        POC
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.include_poc_in_care_plan
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.include_poc_in_care_plan
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-12">
                                        Notes
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.case_note_writeback_enabled
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.case_note_writeback_enabled
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-12">
                                        Link
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            settings.link_client_profile_switches
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {settings.link_client_profile_switches
                                            ? 'Yes'
                                            : 'No'}
                                    </Badge>
                                </div>
                            </div>
                        );
                    }

                    if (dataSourceType === 'generations_homecare') {
                        const settings = row.original
                            .provider_settings as GenerationsProviderSettings | null;
                        if (!settings) {
                            return (
                                <span className="text-muted-foreground/70">
                                    —
                                </span>
                            );
                        }
                        return (
                            <GenerationsProviderSettingsSummary
                                settings={settings}
                            />
                        );
                    }

                    if (dataSourceType === 'axiscare') {
                        const settings = row.original
                            .provider_settings as AxisCareProviderSettings | null;
                        const voiceCallLoggingEnabled =
                            settings?.voice_call_logging_enabled ?? true;
                        const voiceCallWritebackDestination =
                            settings?.voice_call_writeback_destination ??
                            'call_log';
                        const voiceCallWritebackLabel = !voiceCallLoggingEnabled
                            ? 'Disabled'
                            : voiceCallWritebackDestination === 'person_note'
                              ? 'Person Note'
                              : 'Call Log';
                        const includePriorityNote =
                            settings?.include_priority_note ?? false;
                        const includeCarePlanForm =
                            settings?.include_care_plan_form ?? false;
                        const sectionAllowlistLength =
                            settings?.care_plan_form_section_allowlist
                                ?.length ?? 0;
                        const caregiverStatusTagAllowlistLength =
                            settings?.caregiver_status_tag_allowlist?.length ??
                            0;
                        const ignoredCaregiverStatusLabelsLength =
                            settings?.ignored_caregiver_status_labels?.length ??
                            0;
                        const assumeUncheckedCaregiverAttributes =
                            settings?.assume_unchecked_caregiver_attributes ??
                            false;
                        const assumeUncheckedClientAttributes =
                            settings?.assume_unchecked_client_attributes ??
                            false;
                        const modificationReasonRules =
                            row.original.axiscare_modification_reason_rules ??
                            [];
                        return (
                            <div className="flex flex-col gap-0.5 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-20">
                                        Write calls
                                    </span>
                                    <Badge
                                        variant="outline"
                                        aria-label={`Write calls: ${voiceCallWritebackLabel}`}
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            voiceCallLoggingEnabled
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {voiceCallWritebackLabel}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-20">
                                        Priority Note
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            includePriorityNote
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {includePriorityNote
                                            ? 'Enabled'
                                            : 'Disabled'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground w-20">
                                        Care Plan Form
                                    </span>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            'h-5 px-1.5 text-[10px]',
                                            includeCarePlanForm
                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                : '',
                                        )}
                                    >
                                        {includeCarePlanForm
                                            ? 'Enabled'
                                            : 'Disabled'}
                                    </Badge>
                                </div>
                                {sectionAllowlistLength > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground w-20">
                                            Sections
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className="h-5 px-1.5 text-[10px]"
                                        >
                                            {sectionAllowlistLength}
                                        </Badge>
                                    </div>
                                )}
                                {caregiverStatusTagAllowlistLength > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground w-20">
                                            Status Tags
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className="h-5 px-1.5 text-[10px]"
                                        >
                                            {caregiverStatusTagAllowlistLength}
                                        </Badge>
                                    </div>
                                )}
                                {ignoredCaregiverStatusLabelsLength > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground w-20">
                                            Ignored Statuses
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className="h-5 px-1.5 text-[10px]"
                                        >
                                            {ignoredCaregiverStatusLabelsLength}
                                        </Badge>
                                    </div>
                                )}
                                {assumeUncheckedCaregiverAttributes && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground w-20">
                                            CG Attributes
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className="h-5 bg-green-100 px-1.5 text-[10px] text-green-800 dark:bg-green-950/50 dark:text-green-300"
                                        >
                                            Assume Unchecked
                                        </Badge>
                                    </div>
                                )}
                                {assumeUncheckedClientAttributes && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground w-20">
                                            CL Attributes
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className="h-5 bg-green-100 px-1.5 text-[10px] text-green-800 dark:bg-green-950/50 dark:text-green-300"
                                        >
                                            Assume Unchecked
                                        </Badge>
                                    </div>
                                )}
                                <AxisCareModificationReasonRulesBadge
                                    rules={modificationReasonRules}
                                />
                            </div>
                        );
                    }

                    return <span className="text-muted-foreground/70">—</span>;
                },
            },
            {
                accessorKey: 'days_lookback',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Lookback" />
                ),
                cell: ({ row }) => {
                    const value = row.original.days_lookback;
                    return <span>{value} days</span>;
                },
                enableSorting: true,
            },
            {
                accessorKey: 'days_lookforward',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Lookforward"
                    />
                ),
                cell: ({ row }) => {
                    const value = row.original.days_lookforward;
                    return <span>{value} days</span>;
                },
                enableSorting: true,
            },
            {
                accessorKey: 'daily_sync_days_lookforward',
                header: ({ column }) => (
                    <DataTableColumnHeader
                        column={column}
                        title="Daily Lookahead"
                    />
                ),
                cell: ({ row }) => {
                    const value = row.original.daily_sync_days_lookforward;
                    return <span>{value} days</span>;
                },
                enableSorting: true,
            },
            {
                id: 'actions',
                header: '',
                cell: ({ row }) => {
                    return (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                void openSettingsEditor(row.original);
                            }}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    );
                },
            },
        ],
        [openSettingsEditor],
    );

    const currentEditingSettings = useMemo(() => {
        if (!editingSettings) {
            return null;
        }
        return (
            data?.sync_settings.find(
                (syncSettings) =>
                    syncSettings.data_source_account_id ===
                    editingSettings.data_source_account_id,
            ) ?? editingSettings
        );
    }, [data?.sync_settings, editingSettings]);

    const dataSourceTypes = useMemo(() => {
        if (!data?.sync_settings) {
            return [];
        }
        const types = new Set(
            data.sync_settings.map((s) => s.data_source_type),
        );
        return Array.from(types).sort();
    }, [data?.sync_settings]);

    return (
        <div className="flex flex-col p-8">
            <div className="mb-8">
                <div className="flex items-center gap-2">
                    <Settings className="h-6 w-6" />
                    <h1 className="text-foreground text-2xl font-semibold">
                        Sync Settings
                    </h1>
                </div>
                <p className="text-muted-foreground mt-2 text-sm">
                    Manage sync configuration for all organizations and data
                    source accounts.
                </p>
            </div>
            <div className="mb-4 flex items-center gap-4">
                <Input
                    placeholder="Search by organization name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-sm"
                />
                <Select
                    value={dataSourceFilter}
                    onValueChange={(value) =>
                        setDataSourceFilter(value as DataSourceType | 'all')
                    }
                >
                    <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filter by data source" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Data Sources</SelectItem>
                        {dataSourceTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                                {getDataSourceDisplayName(type)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {data && (
                    <span className="text-muted-foreground text-sm">
                        {data.total} account{data.total !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
            <Card className="p-0">
                {isLoading ? (
                    <div className="flex h-64 items-center justify-center">
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading sync settings…</span>
                        </div>
                    </div>
                ) : data && data.sync_settings.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={data.sync_settings}
                        isLoading={isLoading}
                        initialSorting={[
                            { id: 'data_source_type', desc: false },
                        ]}
                        stickyRightColumnId="actions"
                        stickyRightFade={true}
                        renderToolbar={(table) => (
                            <DataTableToolbar
                                table={table}
                                hideGlobalSearch={true}
                            />
                        )}
                    />
                ) : (
                    <div className="text-muted-foreground flex h-64 items-center justify-center">
                        No sync settings found
                    </div>
                )}
            </Card>
            {currentEditingSettings && (
                <EditSyncSettingsDialog
                    settings={currentEditingSettings}
                    open={currentEditingSettings !== null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setEditingSettings(null);
                        }
                    }}
                    onSave={() => mutate()}
                />
            )}
        </div>
    );
}
